import Papa from 'papaparse';

export const normalizeAddress = (value = '') => String(value).trim().replace(/\s+/g, ' ');

export const getDisplayAddress = (value) =>
  normalizeAddress(value).replace(/,\s*(USA|United States(?: of America)?)\.?$/i, '');

export const getAutocompleteDisplayText = (value) =>
  String(value || '').replace(/,\s*(USA|United States(?: of America)?)\b\.?/gi, '');

export const getAddressKey = (value) => normalizeAddress(value).toLowerCase();

export const getAddressMatchKey = (value) =>
  getAddressKey(value)
    .replace(/,\s*(usa|united states(?: of america)?)\.?$/i, '')
    .replace(/(,\s*[a-z]{2})\s+\d{5}(?:-\d{4})?\s*$/, '$1');

export const isMatchingStartingAddress = (
  currentAddress,
  nextAddress,
  { addressConfigs = {}, nextPlaceId = '' } = {}
) => {
  const currentKey = getAddressKey(currentAddress);
  const nextAddressKey = getAddressKey(nextAddress);

  if (nextPlaceId && addressConfigs[currentKey]?.placeId) {
    return addressConfigs[currentKey].placeId === nextPlaceId;
  }

  return currentKey === nextAddressKey;
};

const normalizeContactName = (value) => {
  const text = normalizeAddress(value);

  return text && !['null', 'null null', 'undefined', 'undefined undefined'].includes(text.toLowerCase())
    ? text
    : '';
};

const getContactFullName = (contact = {}) =>
  normalizeContactName(
    contact.fullName && contact.fullName !== 'null'
      ? contact.fullName
      : [contact.firstName, contact.lastName].filter(Boolean).join(' ')
  );

const getContactCompanyName = (contact = {}) =>
  normalizeContactName(
    contact.companyName ||
      contact.company_name ||
      contact.businessName ||
      contact.business_name
  );

export const buildContactAddress = (contact = {}) => {
  const street = normalizeAddress(
    [
      contact.address1 || contact.address || contact.street || contact.streetAddress,
      contact.address2 || contact.unit || contact.suite,
    ].filter(Boolean).join(' ')
  );
  const cityState = [contact.city, contact.state].filter(Boolean).join(', ');

  return normalizeAddress([street, [cityState, contact.zip || contact.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', '));
};

const parseContactMeta = (meta) => {
  if (!meta) return {};
  if (typeof meta === 'object') return meta;

  try {
    return JSON.parse(meta);
  } catch {
    return {};
  }
};

export const getContactDataAxlePlaceIds = (contact = {}) => {
  const meta = parseContactMeta(contact.meta);
  const dataAxle = meta.dataAxle || contact.dataAxle || {};
  const candidates = [
    contact.infogroupId,
    contact.infogroup_id,
    dataAxle.infogroup_id,
    dataAxle.infogroupId,
  ];

  return [...new Set(candidates
    .map((value) => String(value || '').trim())
    .filter(Boolean))];
};

export const buildContactOptionTitle = (contact = {}) => {
  const fullName = getContactFullName(contact);
  const companyName = getContactCompanyName(contact);
  const displayName = fullName || companyName;
  const contactAddress = buildContactAddress(contact);

  return normalizeAddress([displayName, contactAddress].filter(Boolean).join(', ')) || `Contact #${contact.id}`;
};

const sampleAddressUploadCsv = [
  'address',
  '"1600 Pennsylvania Avenue NW, Washington, DC 20500"',
  '"350 Fifth Avenue, New York, NY 10118"',
  '"1 Apple Park Way, Cupertino, CA 95014"',
].join('\n');

const addressUploadHeaderAliases = {
  city: [
    'city',
    'mailing city',
    'property city',
    'site city',
    'physical city',
    'situs city',
    'town',
  ],
  fullAddress: [
    'full address',
    'full_address',
    'formatted address',
    'formatted_address',
    'complete address',
    'complete_address',
    'property full address',
    'property_full_address',
    'mailing full address',
    'mailing_full_address',
    'starting address',
    'starting_address',
    'location',
  ],
  state: [
    'state',
    'st',
    'mailing state',
    'property state',
    'site state',
    'physical state',
    'situs state',
    'province',
  ],
  street: [
    'address',
    'address 1',
    'address_1',
    'address1',
    'address line 1',
    'address_line_1',
    'line 1',
    'line_1',
    'mailing address',
    'mailing_address',
    'mailing address 1',
    'mailing_address_1',
    'mailing address line 1',
    'mailing_address_line_1',
    'property address',
    'property_address',
    'property address 1',
    'property_address_1',
    'property street',
    'site address',
    'site_address',
    'physical address',
    'physical_address',
    'situs address',
    'situs_address',
    'street',
    'street address',
    'street_address',
  ],
  street2: [
    'address 2',
    'address_2',
    'address2',
    'address line 2',
    'address_line_2',
    'line 2',
    'line_2',
    'mailing address 2',
    'mailing_address_2',
    'mailing address line 2',
    'mailing_address_line_2',
    'property address 2',
    'property_address_2',
    'site address 2',
    'site_address_2',
    'physical address 2',
    'physical_address_2',
    'unit',
    'suite',
    'apt',
    'apartment',
  ],
  zip: [
    'zip',
    'zip code',
    'zipcode',
    'postal',
    'postal code',
    'postal_code',
    'mailing zip',
    'mailing_zip',
    'mailing zip code',
    'mailing_zip_code',
    'property zip',
    'property_zip',
    'property zip code',
    'site zip',
    'physical zip',
    'situs zip',
  ],
};

const normalizeAddressUploadHeader = (value = '') =>
  normalizeAddress(value)
    .replace(/^\uFEFF/, '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');

const addressUploadHeaderLookup = Object.entries(addressUploadHeaderAliases).reduce((lookup, [key, aliases]) => {
  aliases.forEach((alias) => {
    lookup[normalizeAddressUploadHeader(alias)] = key;
  });

  return lookup;
}, {});

const getAddressUploadHeaderKey = (header = '') => {
  const normalizedHeader = normalizeAddressUploadHeader(header);
  if (!normalizedHeader) return '';
  if (addressUploadHeaderLookup[normalizedHeader]) return addressUploadHeaderLookup[normalizedHeader];

  if (/email|phone|first|last|name|company|business|notes?|tag|id$/.test(normalizedHeader)) return '';
  if (/city|town|municipality/.test(normalizedHeader)) return 'city';
  if (/state|province|region/.test(normalizedHeader)) return 'state';
  if (/zip|zipcode|postal|postcode/.test(normalizedHeader)) return 'zip';
  if (/(unit|suite|apt|apartment|address2|addr2|line2)/.test(normalizedHeader)) return 'street2';
  if (/(full|complete|formatted)/.test(normalizedHeader) && /(address|addr|location)/.test(normalizedHeader)) {
    return 'fullAddress';
  }
  if (/(address|addr|street|situs|mailing|property|physical|site)/.test(normalizedHeader)) return 'street';

  return '';
};

const getAddressUploadHeaderMap = (headers = []) => {
  const headerMap = {};

  headers.forEach((header, index) => {
    const key = getAddressUploadHeaderKey(header);

    if (!key) return;

    if (!headerMap[key]) headerMap[key] = [];
    headerMap[key].push(index);
  });

  return headerMap;
};

const getFirstUploadCellValue = (row = [], indexes = []) => {
  const matchedIndex = indexes.find((index) => normalizeAddress(row[index]));
  return matchedIndex === undefined ? '' : normalizeAddress(row[matchedIndex]);
};

const joinCityStateZip = ({ city = '', state = '', zip = '' } = {}) => {
  const cityState = [city, state].filter(Boolean).join(', ');

  return normalizeAddress([cityState, zip].filter(Boolean).join(' '));
};

const buildAddressUploadRowValue = (row = [], headerMap = {}) => {
  const fullAddress = getFirstUploadCellValue(row, headerMap.fullAddress || []);
  const street = getFirstUploadCellValue(row, headerMap.street || []);
  const street2 = getFirstUploadCellValue(row, headerMap.street2 || []);
  const city = getFirstUploadCellValue(row, headerMap.city || []);
  const state = getFirstUploadCellValue(row, headerMap.state || []);
  const zip = getFirstUploadCellValue(row, headerMap.zip || []);
  const hasSplitLocation = Boolean(city || state || zip);

  if (street && hasSplitLocation) {
    return normalizeAddress([
      street,
      street2,
      joinCityStateZip({ city, state, zip }),
    ].filter(Boolean).join(', '));
  }

  if (fullAddress) return fullAddress;
  if (street) return normalizeAddress([street, street2].filter(Boolean).join(', '));

  return '';
};

const getFallbackUploadRowAddress = (row = []) => {
  const cells = row.map(normalizeAddress).filter(Boolean);
  if (!cells.length) return '';
  if (cells.length === 1) return cells[0];

  return normalizeAddress(cells.join(', '));
};

export const parseUploadedAddressText = (text = '') => {
  const parsedCsv = Papa.parse(String(text || ''), {
    skipEmptyLines: 'greedy',
  });
  const rows = (parsedCsv.data || [])
    .map((row) => (Array.isArray(row) ? row.map(normalizeAddress) : []))
    .filter((row) => row.some(Boolean));

  if (!rows.length) {
    return {
      addresses: [],
      parsedCount: 0,
      rowCount: 0,
      skippedCount: 0,
    };
  }

  const headerMap = getAddressUploadHeaderMap(rows[0]);
  const hasHeader = Object.keys(headerMap).length > 0;
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const addresses = dataRows
    .map((row) => (hasHeader ? buildAddressUploadRowValue(row, headerMap) : getFallbackUploadRowAddress(row)))
    .map(normalizeAddress)
    .filter(Boolean);

  return {
    addresses,
    parsedCount: addresses.length,
    rowCount: dataRows.length,
    skippedCount: Math.max(dataRows.length - addresses.length, 0),
  };
};

export const getUniqueAddresses = (addresses = []) => {
  const seenAddressKeys = new Set();

  return addresses.filter((address) => {
    const addressKey = getAddressMatchKey(address);
    if (!addressKey || seenAddressKeys.has(addressKey)) return false;

    seenAddressKeys.add(addressKey);
    return true;
  });
};

export const downloadSampleAddressUploadCsv = () => {
  if (typeof document === 'undefined') return;

  const blob = new Blob([sampleAddressUploadCsv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'sample-addresses.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};
